// Shared Kontakter / Opgaver / Dokumenter / Billeder surface tabs.
// Parameterised by `basePath` so the SAME components serve both the ejendom
// (`/api/v1/properties/{id}`) and the lejemål (`/api/v1/leases/{id}`) pages.

import React, { useEffect, useState } from 'react'
import { Chip } from '../ui/Chip'
import { Button } from '../ui/Button'
import { apiFetch, apiFetchRaw, ApiError } from '../../lib/api-client'

const tabCard: React.CSSProperties = { background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }
const tabHeader: React.CSSProperties = { padding: '14px 24px', borderBottom: '1px solid #e8eaed', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const tabTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }
const fieldInput: React.CSSProperties = { width: '100%', height: 34, padding: '0 10px', borderRadius: 7, border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a', background: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }
const thStyle: React.CSSProperties = { padding: '10px 24px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' }
const tdStyle: React.CSSProperties = { padding: '11px 24px', fontSize: 13, color: '#111827' }

// ─── Kontakter ────────────────────────────────────────────────────────────────

interface SurfaceContact {
  id: string
  contact_type: string
  contact_person: string | null
  contact_company: string | null
  email: string | null
  phone: string | null
}

export function KontakterTab({ basePath }: { basePath: string }) {
  const [contacts, setContacts] = useState<SurfaceContact[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ contact_type: '', contact_person: '', contact_company: '', email: '', phone: '' })
  const [saving, setSaving] = useState(false)
  const base = `${basePath}/contacts`

  useEffect(() => {
    apiFetch<SurfaceContact[]>(base).then(setContacts).catch(() => {}).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath])

  const add = async () => {
    if (!form.contact_type.trim()) return
    setSaving(true)
    try {
      const created = await apiFetch<SurfaceContact>(base, { method: 'POST', body: JSON.stringify(form) })
      setContacts((c) => [...c, created])
      setForm({ contact_type: '', contact_person: '', contact_company: '', email: '', phone: '' })
    } catch { /* surfaced by global handler */ } finally { setSaving(false) }
  }

  const remove = async (id: string) => {
    await apiFetch(`${base}/${id}`, { method: 'DELETE' })
    setContacts((c) => c.filter((x) => x.id !== id))
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>

  return (
    <div style={tabCard}>
      <div style={tabHeader}><span style={tabTitle}>Kontakter</span></div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {['Type', 'Person', 'Virksomhed', 'E-mail', 'Telefon', ''].map((h, i) => (<th key={i} style={thStyle}>{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.id} style={{ borderTop: '1px solid #f3f4f6' }}>
              <td style={{ ...tdStyle, fontWeight: 500 }}>{c.contact_type}</td>
              <td style={tdStyle}>{c.contact_person ?? '—'}</td>
              <td style={tdStyle}>{c.contact_company ?? '—'}</td>
              <td style={tdStyle}>{c.email ?? '—'}</td>
              <td style={tdStyle}>{c.phone ?? '—'}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <button type="button" onClick={() => remove(c.id)} style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>Slet</button>
              </td>
            </tr>
          ))}
          {contacts.length === 0 && (
            <tr><td colSpan={6} style={{ ...tdStyle, color: '#9ca3af' }}>Ingen kontakter registreret endnu.</td></tr>
          )}
          <tr style={{ borderTop: '1px solid #f3f4f6', background: '#fcfcfd' }}>
            <td style={{ padding: '8px 24px' }}><input style={fieldInput} placeholder="Type *" value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} /></td>
            <td style={{ padding: '8px 12px' }}><input style={fieldInput} placeholder="Person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></td>
            <td style={{ padding: '8px 12px' }}><input style={fieldInput} placeholder="Virksomhed" value={form.contact_company} onChange={(e) => setForm({ ...form, contact_company: e.target.value })} /></td>
            <td style={{ padding: '8px 12px' }}><input style={fieldInput} placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></td>
            <td style={{ padding: '8px 12px' }}><input style={fieldInput} placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></td>
            <td style={{ padding: '8px 24px', textAlign: 'right' }}><Button size="sm" onClick={add} isLoading={saving} disabled={!form.contact_type.trim()}>Tilføj</Button></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ─── Opgaver ──────────────────────────────────────────────────────────────────

interface SurfaceTask {
  id: string
  title: string
  status: string
  due_date: string | null
}

export function OpgaverTab({ basePath }: { basePath: string }) {
  const [tasks, setTasks] = useState<SurfaceTask[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ title: '', due_date: '' })
  const [saving, setSaving] = useState(false)
  const base = `${basePath}/tasks`

  useEffect(() => {
    apiFetch<SurfaceTask[]>(base).then(setTasks).catch(() => {}).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath])

  const reload = () => apiFetch<SurfaceTask[]>(base).then(setTasks).catch(() => {})

  const add = async () => {
    if (!form.title.trim()) return
    setSaving(true)
    try {
      await apiFetch(base, { method: 'POST', body: JSON.stringify({ title: form.title, due_date: form.due_date || null }) })
      setForm({ title: '', due_date: '' })
      await reload()
    } catch { /* surfaced by global handler */ } finally { setSaving(false) }
  }

  const toggle = async (t: SurfaceTask) => {
    await apiFetch(`${base}/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: t.status === 'done' ? 'open' : 'done' }) })
    await reload()
  }

  const remove = async (id: string) => {
    await apiFetch(`${base}/${id}`, { method: 'DELETE' })
    setTasks((c) => c.filter((x) => x.id !== id))
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>

  return (
    <div style={tabCard}>
      <div style={tabHeader}><span style={tabTitle}>Opgaver</span></div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {['', 'Opgave', 'Forfald', 'Status', ''].map((h, i) => (<th key={i} style={thStyle}>{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const done = t.status === 'done'
            return (
              <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ ...tdStyle, width: 36 }}>
                  <input type="checkbox" checked={done} onChange={() => toggle(t)} style={{ cursor: 'pointer' }} />
                </td>
                <td style={{ ...tdStyle, fontWeight: 500, textDecoration: done ? 'line-through' : 'none', color: done ? '#9ca3af' : '#111827' }}>{t.title}</td>
                <td style={tdStyle}>{t.due_date ?? '—'}</td>
                <td style={tdStyle}><Chip variant={done ? 'green' : 'amber'}>{done ? 'Færdig' : 'Åben'}</Chip></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button type="button" onClick={() => remove(t.id)} style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>Slet</button>
                </td>
              </tr>
            )
          })}
          {tasks.length === 0 && (
            <tr><td colSpan={5} style={{ ...tdStyle, color: '#9ca3af' }}>Ingen opgaver endnu.</td></tr>
          )}
          <tr style={{ borderTop: '1px solid #f3f4f6', background: '#fcfcfd' }}>
            <td />
            <td style={{ padding: '8px 24px' }}><input style={fieldInput} placeholder="Ny opgave *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></td>
            <td style={{ padding: '8px 12px' }}><input style={fieldInput} type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></td>
            <td />
            <td style={{ padding: '8px 24px', textAlign: 'right' }}><Button size="sm" onClick={add} isLoading={saving} disabled={!form.title.trim()}>Tilføj</Button></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ─── Dokumenter + Billeder ───────────────────────────────────────────────────

interface SurfaceFile {
  id: string
  kind: string
  filename: string
  file_size: number
  created_at: string
  url: string
  tenant_visible?: boolean
}

function bytesToHuman(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function AuthImg({ path, alt, style }: { path: string; alt: string; style: React.CSSProperties }) {
  const [src, setSrc] = useState<string>()
  useEffect(() => {
    let alive = true
    let url: string | undefined
    apiFetchRaw(path).then((r) => r.blob()).then((b) => { if (alive) { url = URL.createObjectURL(b); setSrc(url) } }).catch(() => {})
    return () => { alive = false; if (url) URL.revokeObjectURL(url) }
  }, [path])
  if (!src) return <div style={{ ...style, background: '#f1f5f9' }} />
  return <img src={src} alt={alt} style={style} />
}

async function openFile(path: string) {
  const resp = await apiFetchRaw(path)
  if (!resp.ok) return
  const url = URL.createObjectURL(await resp.blob())
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function useFiles(basePath: string, kind: 'dokument' | 'billede') {
  const [files, setFiles] = useState<SurfaceFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const base = `${basePath}/files`

  useEffect(() => {
    apiFetch<SurfaceFile[]>(`${base}?kind=${kind}`).then(setFiles).catch(() => {}).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, kind])

  const upload = async (file: File) => {
    setUploading(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const created = await apiFetch<SurfaceFile>(`${base}?kind=${kind}`, { method: 'POST', body: fd })
      setFiles((f) => [created, ...f])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload fejlede.')
    } finally {
      setUploading(false)
    }
  }

  const remove = async (id: string) => {
    await apiFetch(`${base}/${id}`, { method: 'DELETE' })
    setFiles((f) => f.filter((x) => x.id !== id))
  }

  // Lease documents only — toggle visibility in the tenant portal.
  const setVisibility = async (id: string, tenant_visible: boolean) => {
    const updated = await apiFetch<SurfaceFile>(`${base}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ tenant_visible }),
    })
    setFiles((f) => f.map((x) => (x.id === id ? { ...x, tenant_visible: updated.tenant_visible } : x)))
  }

  return { files, loading, uploading, error, upload, remove, setVisibility }
}

export function DokumenterTab({ basePath }: { basePath: string }) {
  const { files, loading, uploading, error, upload, remove, setVisibility } = useFiles(basePath, 'dokument')
  const inputRef = React.useRef<HTMLInputElement>(null)
  // The "Del med lejer" toggle only applies to lease documents (the tenant portal
  // is lease-scoped); property documents have no tenant.
  const isLease = basePath.includes('/leases/')
  const cols = isLease ? 5 : 4

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>

  return (
    <div style={tabCard}>
      <div style={tabHeader}>
        <span style={tabTitle}>Dokumenter</span>
        <Button size="sm" onClick={() => inputRef.current?.click()} isLoading={uploading}>+ Upload</Button>
        <input ref={inputRef} type="file" accept=".pdf,image/*,.docx,.xlsx" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = '' }} />
      </div>
      {error && <div style={{ padding: '8px 24px', fontSize: 12, color: '#dc2626' }}>{error}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {['Filnavn', 'Størrelse', 'Tilføjet', ...(isLease ? ['Del med lejer'] : []), ''].map((h, i) => (<th key={i} style={thStyle}>{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.id} style={{ borderTop: '1px solid #f3f4f6' }}>
              <td style={{ ...tdStyle, fontWeight: 500 }}>
                <button type="button" onClick={() => openFile(f.url)} style={{ border: 'none', background: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 13, padding: 0 }}>{f.filename}</button>
              </td>
              <td style={tdStyle}>{bytesToHuman(f.file_size)}</td>
              <td style={tdStyle}>{f.created_at.slice(0, 10)}</td>
              {isLease && (
                <td style={tdStyle}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: f.tenant_visible ? '#16a34a' : '#6b7280' }}>
                    <input type="checkbox" checked={!!f.tenant_visible} onChange={(e) => setVisibility(f.id, e.target.checked)} style={{ cursor: 'pointer' }} />
                    {f.tenant_visible ? 'Delt' : 'Privat'}
                  </label>
                </td>
              )}
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <button type="button" onClick={() => remove(f.id)} style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>Slet</button>
              </td>
            </tr>
          ))}
          {files.length === 0 && (
            <tr><td colSpan={cols} style={{ ...tdStyle, color: '#9ca3af' }}>Ingen dokumenter endnu. Upload PDF, billede eller Office-fil.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function BillederTab({ basePath }: { basePath: string }) {
  const { files, loading, uploading, error, upload, remove } = useFiles(basePath, 'billede')
  const inputRef = React.useRef<HTMLInputElement>(null)

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>

  return (
    <div style={tabCard}>
      <div style={tabHeader}>
        <span style={tabTitle}>Billeder</span>
        <Button size="sm" onClick={() => inputRef.current?.click()} isLoading={uploading}>+ Upload</Button>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = '' }} />
      </div>
      {error && <div style={{ padding: '8px 24px', fontSize: 12, color: '#dc2626' }}>{error}</div>}
      <div style={{ padding: 20 }}>
        {files.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 32 }}>Ingen billeder endnu.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
            {files.map((f) => (
              <div key={f.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #e8eaed' }}>
                <button type="button" onClick={() => openFile(f.url)} style={{ border: 'none', padding: 0, background: 'none', cursor: 'pointer', display: 'block', width: '100%' }}>
                  <AuthImg path={f.url} alt={f.filename} style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px' }}>
                  <span style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
                  <button type="button" onClick={() => remove(f.id)} style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>Slet</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
